import { Injectable, computed, signal } from '@angular/core'

const STORAGE_KEY = 'gbc-dossier-layout'

/** Dispositions possibles de la page détails d'un dossier. */
export type DossierLayout = 'lateral' | 'compact'

/** Disposition par défaut (utilisateur qui n'a jamais choisi) : nav latérale. */
const DEFAULT_LAYOUT: DossierLayout = 'lateral'

/**
 * Préférence d'affichage de la page dossier, choisie par l'utilisateur via le
 * switch en en-tête de page. Persistée par navigateur (localStorage), sur le
 * même modèle que {@link TextSizeService}.
 * - `lateral`  : navigation de section en barre latérale, alertes intégrées.
 * - `compact`  : navigation de section horizontale + bloc alertes séparé.
 */
@Injectable({ providedIn: 'root' })
export class DossierLayoutService {
  private readonly layoutSig = signal<DossierLayout>(this.restore())

  readonly layout = this.layoutSig.asReadonly()
  readonly isLateral = computed(() => this.layoutSig() === 'lateral')
  readonly isCompact = computed(() => this.layoutSig() === 'compact')

  set(layout: DossierLayout): void {
    this.layoutSig.set(layout)
    try {
      localStorage.setItem(STORAGE_KEY, layout)
    } catch {
      // stockage indisponible (navigation privée) : le choix reste appliqué pour la session
    }
  }

  toggle(): void {
    this.set(this.layoutSig() === 'lateral' ? 'compact' : 'lateral')
  }

  private restore(): DossierLayout {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw === 'lateral' || raw === 'compact' ? raw : DEFAULT_LAYOUT
    } catch {
      return DEFAULT_LAYOUT
    }
  }
}
